import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Network, Plus, Search, Trash2, Upload, Image } from 'lucide-react';

import { BACKEND_URL } from '@/lib/backend';
export default function NetworkDiagramsModule() {
  const [diagrams, setDiagrams] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    organization_id: '',
    title: '',
    description: '',
    file_url: '',
    file_type: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [diagRes, orgRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/documentation/network-diagrams`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/documentation/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      if (diagRes.ok) setDiagrams(await diagRes.json());
      if (orgRes.ok) setOrganizations(await orgRes.json());
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/documentation/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData({
          ...formData,
          file_url: data.file_url,
          file_type: data.file_type
        });
      } else {
        alert('File upload failed');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      alert('File upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.file_url) {
      alert('Please upload a file first');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/network-diagrams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setShowAddDialog(false);
        setFormData({ organization_id: '', title: '', description: '', file_url: '', file_type: '' });
        loadData();
      }
    } catch (err) {
      console.error('Failed to create:', err);
    }
  };

  const handleDelete = async (id, title) => {
    if (!confirm(`Delete diagram: ${title}?`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/network-diagrams/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const filtered = diagrams.filter(d =>
    d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.organization_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Network Diagrams</h1>
          <p className="text-sm text-slate-500 mt-1">Network topology & infrastructure diagrams</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />Upload Diagram
          </Button>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Upload Network Diagram</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Organization *</Label>
                <Select value={formData.organization_id} onValueChange={(v) => setFormData({...formData, organization_id: v})} required>
                  <SelectTrigger><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-4">
                <div><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required /></div>
                <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} /></div>
                <div>
                  <Label>Upload File *</Label>
                  <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                    {formData.file_url ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <Image className="w-5 h-5" />
                        <span>File uploaded successfully</span>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-slate-600">Click to upload image/PDF</p>
                        <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleFileUpload} />
                      </label>
                    )}
                    {uploading && <p className="text-sm text-cyan-600 mt-2">Uploading...</p>}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={uploading || !formData.file_url}>Upload Diagram</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-6"><CardContent className="p-4"><div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" /><Input placeholder="Search diagrams..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" /></div></CardContent></Card>

      {loading ? <div className="text-center py-12"><p className="text-slate-500">Loading...</p></div> : filtered.length === 0 ? <Card><CardContent className="p-12 text-center"><Network className="w-12 h-12 mx-auto text-slate-400 mb-3" /><p className="text-slate-600 font-medium">No diagrams found</p></CardContent></Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((diagram) => (
            <Card key={diagram.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <Network className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-slate-800">{diagram.title}</h3>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(diagram.id, diagram.title)}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
                <div className="mb-3 bg-slate-100 rounded-lg h-40 flex items-center justify-center">
                  {diagram.file_type?.includes('image') ? (
                    <img src={`${BACKEND_URL}${diagram.file_url}`} alt={diagram.title} className="max-h-full max-w-full object-contain" />
                  ) : (
                    <div className="text-center">
                      <Image className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                      <p className="text-xs text-slate-500">PDF Document</p>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-2">{diagram.organization_name}</p>
                {diagram.description && <p className="text-sm text-slate-600 mb-2">{diagram.description}</p>}
                <p className="text-xs text-slate-400">By: {diagram.created_by_name} • {new Date(diagram.created_at).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
